import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { showDate } from '../../../shared/tree'
import { Avatar } from '@/components/tree/Avatar'
import { albumPhotoUrl, peopleIn, type Album, type AlbumPhoto } from '@/lib/albums'
import { bookPages, coverOf, OTHER_TIMES, type BookPage } from '@/lib/storybook'
import { cn } from '@/lib/utils'
import { StoryImg } from './PickedUp'

// How far a page turns before it's gone (a little past upright, so it falls away), and how much of the page's width
// the reader must drag it to turn it.
const GONE = -100
const ENOUGH = 0.2
// Pages kept in the page around the one open; the rest of a long album isn't drawn until it's near.
const NEAR = 2

// The album as a book whose pages turn under the reader's finger. A page follows the drag halfway if the reader
// hesitates, and falls back if they let go too soon. Tapping a page's edge turns it too; tapping a photo picks it up.
export function Book({ album, onPick }: { album: Album; onPick: (photo: AlbumPhoto) => void }) {
  const pages = bookPages(album)
  const [open, setOpen] = useState(0)
  const leaves = useRef(new Map<number, HTMLElement>())
  const book = useRef<HTMLDivElement>(null)
  const thumbs = useRef<HTMLDivElement>(null)
  const last = pages.length - 1

  const lay = (index: number, degrees: number, turning: boolean) => {
    const leaf = leaves.current.get(index)
    if (!leaf) return
    leaf.classList.toggle('turning', turning)
    leaf.style.transform = `rotateY(${degrees}deg)`
    const gloss = leaf.querySelector<HTMLElement>('.sb-gloss')
    if (gloss) gloss.style.opacity = String(Math.min(1, Math.abs(degrees) / 90))
  }
  const go = (to: number) => setOpen(Math.max(0, Math.min(last, to)))

  // Every page before the open one has turned; the open one and those after lie flat.
  useEffect(() => {
    for (const index of leaves.current.keys()) lay(index, index < open ? GONE : 0, true)
    thumbs.current?.querySelector(`[data-page="${open}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [open])

  const drag = useRef<{ x: number; dx: number; moved: boolean } | null>(null)
  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, dx: 0, moved: false }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    d.dx = e.clientX - d.x
    if (Math.abs(d.dx) > 6) d.moved = true
    if (!d.moved) return
    const width = book.current?.clientWidth ?? 1
    if (d.dx < 0 && open < last) lay(open, Math.max(GONE, (d.dx / width) * 140), false)
    if (d.dx > 0 && open > 0) lay(open - 1, Math.min(0, GONE + (d.dx / width) * 140), false)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    drag.current = null
    if (!d || !book.current) return
    const width = book.current.clientWidth
    if (d.moved) {
      if (d.dx < -width * ENOUGH && open < last) go(open + 1)
      else if (d.dx > width * ENOUGH && open > 0) go(open - 1)
      else {
        lay(open, 0, true)
        if (open > 0) lay(open - 1, GONE, true)
      }
      return
    }
    const photo = (e.target as HTMLElement).closest<HTMLElement>('[data-photo]')
    const x = e.clientX - book.current.getBoundingClientRect().left
    if (photo && x > width * 0.15 && x < width * 0.85) {
      const picked = album.photos.find((p) => p.id === photo.dataset.photo)
      if (picked) onPick(picked)
    } else if (x >= width * 0.55) go(open + 1)
    else if (x <= width * 0.45) go(open - 1)
  }

  return (
    <div
      className="flex h-full flex-col gap-3 px-4 pt-[calc(var(--safe-top)+4rem)] pb-[calc(var(--safe-bottom)+0.75rem)] outline-none"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') go(open + 1)
        if (e.key === 'ArrowLeft') go(open - 1)
      }}
      data-testid="story-book"
    >
      <div className="relative min-h-0 flex-1">
        <div
          ref={book}
          className="sb-book"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            drag.current = null
            lay(open, 0, true)
          }}
        >
          {pages.map((page, index) =>
            Math.abs(index - open) > NEAR ? null : (
              <section
                key={index}
                ref={(el) => {
                  if (el) leaves.current.set(index, el)
                  else leaves.current.delete(index)
                }}
                className={cn('sb-leaf', page.kind === 'cover' && 'sb-leaf-cover')}
                style={
                  {
                    zIndex: pages.length - index,
                    transform: `rotateY(${index < open ? GONE : 0}deg)`,
                    visibility: index < open - 1 ? 'hidden' : undefined,
                    pointerEvents: index < open ? 'none' : undefined,
                  } as CSSProperties
                }
                aria-hidden={index !== open}
                inert={index !== open}
                aria-label={index === 0 ? 'Cover' : `Page ${index}`}
              >
                <Page page={page} album={album} onPick={onPick} />
                {index > 0 && <span className="sb-folio">{index}</span>}
                <span className="sb-gloss" />
              </section>
            ),
          )}
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span aria-live="polite">{open === 0 ? 'Cover' : `Page ${open} of ${last}`}</span>
        <span>Swipe, or tap the edge of the page</span>
      </div>
      <div ref={thumbs} className="-mx-1 flex [scrollbar-width:none] gap-1.5 overflow-x-auto px-1 py-0.5">
        {pages.map((page, index) => {
          const photo = page.kind === 'photos' ? page.photos[0] : page.kind === 'cover' ? coverOf(album) : null
          return (
            <button
              key={index}
              type="button"
              data-page={index}
              aria-label={index === 0 ? 'Go to the cover' : `Go to page ${index}`}
              aria-current={index === open ? 'page' : undefined}
              onClick={() => go(index)}
              className={cn(
                'h-11 w-8 shrink-0 rounded-sm bg-secondary bg-cover bg-center opacity-55 transition-opacity outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
                index === open && 'opacity-100 ring-2 ring-foreground',
              )}
              style={photo ? { backgroundImage: `url("${albumPhotoUrl(photo.ref, 'thumb')}")` } : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

function Page({ page, album, onPick }: { page: BookPage; album: Album; onPick: (photo: AlbumPhoto) => void }) {
  if (page.kind === 'cover') {
    const cover = coverOf(album)
    return (
      <div className="relative flex h-full flex-col justify-end overflow-hidden rounded-[inherit] bg-viewer text-viewer-foreground">
        {cover && (
          <StoryImg
            photo={cover}
            sizes="(min-width: 40rem) 36rem, 100vw"
            eager
            className="absolute inset-0 size-full object-cover"
            alt=""
          />
        )}
        <div className="relative flex flex-col gap-1.5 bg-gradient-to-t from-viewer/85 to-transparent px-6 pt-16 pb-9">
          {album.collection && <p className="eyebrow text-viewer-foreground/80">{album.collection.title}</p>}
          <h2 className="text-display text-balance">{album.title}</h2>
          {album.by && <p className="text-sm text-viewer-foreground/85">From the album of {album.by}</p>}
        </div>
      </div>
    )
  }
  if (page.kind === 'words')
    return (
      <div className="flex flex-1 flex-col justify-center gap-4">
        <p className="eyebrow text-sheet-muted">A few words</p>
        <blockquote className="text-xl leading-snug font-medium tracking-tight text-pretty sm:text-2xl">
          {album.description.trim()}
        </blockquote>
        {album.by && <p className="text-sm text-sheet-muted">{album.by}</p>}
      </div>
    )
  if (page.kind === 'people')
    return (
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto">
        <p className="eyebrow text-sheet-muted">The people in this book</p>
        <ul className="grid grid-cols-2 gap-4">
          {peopleIn(album.photos).map((person) => (
            <li key={person.id} className="flex items-center gap-3">
              <Avatar person={person} className="size-10 text-sm" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{person.name}</span>
                <span className="block text-xs text-sheet-muted">
                  {person.living === false ? 'In memory · ' : ''}
                  {person.photos === 1 ? '1 photo' : `${person.photos} photos`}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-auto text-sm text-sheet-muted">
          {album.by ? `From the album of ${album.by}. ` : ''}Every photo is in the album as it was added.
        </p>
      </div>
    )
  // Every photo on a page keeps the same room for words beneath it, so photos side by side line up.
  const words = page.photos.some((x) => x.caption || x.takenOn)
  return (
    <>
      {page.opens && <p className="text-center eyebrow text-sheet-muted">{page.year ?? OTHER_TIMES}</p>}
      <div className="sb-page-body" data-count={page.photos.length}>
        {page.photos.map((photo) => (
          <figure
            key={photo.id}
            data-photo={photo.id}
            className="sb-plate cursor-pointer"
            style={
              {
                '--ratio': photo.width / photo.height,
                '--words': words ? '2.5rem' : '0px',
              } as CSSProperties
            }
          >
            <StoryImg
              photo={photo}
              sizes={page.photos.length === 1 ? '(min-width: 40rem) 32rem, 90vw' : '(min-width: 40rem) 16rem, 45vw'}
            />
            {words && (
              <figcaption className="line-clamp-2 h-[2.75em] text-center text-xs leading-snug text-sheet-muted">
                {photo.caption && <span className="font-medium text-sheet-ink">{photo.caption} </span>}
                {photo.takenOn && showDate(photo.takenOn)}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
      {/* Keyboard readers pick a photo up from here, since the page itself is turned by pointer. */}
      <div className="sr-only">
        {page.photos.map((photo) => (
          <button key={photo.id} type="button" onClick={() => onPick(photo)}>
            Pick up {photo.caption || 'this photo'}
          </button>
        ))}
      </div>
    </>
  )
}
